import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { notificationService } from "../modules/izilife-pay/services/notificationService";
import { CheckCircle, AlertCircle, Mail, Loader2 } from "lucide-react";
import SEO from "../components/common/ui/SEO";

const UnsubscribePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error" | "invalid">("loading");
  const [message, setMessage] = useState("Traitement en cours...");

  const email = searchParams.get("email") || "";
  const userId = searchParams.get("user_id") || "";

  useEffect(() => {
    const processUnsubscribe = async () => {
      if (!email || !email.includes("@")) {
        setStatus("invalid");
        setMessage("Lien de désinscription invalide. Veuillez vérifier l'adresse email.");
        return;
      }

      try {
        const response = await notificationService.publicUnsubscribe(email, userId || undefined);
        setStatus("success");
        setMessage(response.message || "Vous avez été désinscrit avec succès.");
      } catch (error: any) {
        setStatus("error");
        setMessage(error.message || "Une erreur est survenue lors de la désinscription.");
      }
    };

    processUnsubscribe();
  }, [email, userId]);

  const renderIcon = () => {
    switch (status) {
      case "success":
        return <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />;
      case "error":
      case "invalid":
        return <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />;
      default:
        return <Loader2 className="w-16 h-16 text-green-500 mx-auto mb-4 animate-spin" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <SEO
        title="Désinscription | IZI Life Pay"
        description="Confirmez votre désinscription aux emails IZI Life Pay."
        favicon="/favicon.ico"
      />
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des préférences email</h1>
        </div>

        {renderIcon()}

        <p className="text-lg font-medium text-gray-800 mb-2">{message}</p>

        {status === "success" && (
          <p className="text-sm text-gray-600 mb-6">
            L'adresse <strong className="text-gray-900">{email}</strong> ne recevra plus d'emails de la part d'IZI Life
            Pay.
          </p>
        )}

        {status === "error" && (
          <p className="text-sm text-gray-600 mb-6">
            Vous pouvez réessayer plus tard ou contacter le support si le problème persiste.
          </p>
        )}

        {status === "invalid" && (
          <p className="text-sm text-gray-600 mb-6">
            Le lien doit contenir une adresse email valide. Si vous pensez qu'il s'agit d'une erreur, veuillez contacter
            notre support.
          </p>
        )}

        <a
          href="https://izilifepay.com"
          className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
        >
          Retour au site IZI Life Pay
        </a>
      </div>
    </div>
  );
};

export default UnsubscribePage;
